/**
 * The middle pane while a real mail folder is open.
 *
 * Deliberately the same shape as the thread list next to it - one row per
 * message, newest first - because it takes the place of that list rather than
 * sitting beside it. What it does not carry is search and filters: those query
 * threads the CRM stores. The right-click menu it does carry, and its entries
 * act on the mail server itself - see `ImapMessageItems` for what each one
 * does and `useImapMessageActions` for how. This file only draws the rows and
 * raises the menu on them.
 *
 * The head says which folder this is and how much of it arrived. A pane showing
 * fifty of eight thousand messages without saying so is read as the whole
 * folder, and the operator concludes the mail they are hunting for is gone.
 */
import { Loader2, Paperclip, RefreshCw } from "lucide-react";
import { forwardRef, type ReactNode } from "react";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import type { ImapMessageRow } from "../../../../lib/mail";
import { Avatar, Btn, IconBtn, fmtNum, fmtStamp } from "../../../ui";
import { RailTrigger } from "../../_shared/workMobile";
import { ReadingPaneToggle } from "../../_shared/ReadingPaneToggle";
import type { ReadingPane } from "../../_shared/readingPane";
import { ContextMenu, useContextMenu } from "../../../shell/ContextMenu";
import { imapReasonText } from "./imapFolderErrors";

export const ImapMessageList = forwardRef<
  HTMLDivElement,
  {
    path: string;
    rows: ImapMessageRow[];
    total: number;
    loading: boolean;
    reason: string | null;
    openUid: number | null;
    hasMore: boolean;
    onOpen: (uid: number) => void;
    onShowMore: () => void;
    onReload: () => void;
    readingPane: ReadingPane;
    onReadingPane: (next: ReadingPane) => void;
    /** Opens the folder rail. Only reachable on a phone, where it is a sheet. */
    onRail: () => void;
    /** The right-click menu's entries for one row. Without it the rows raise none. */
    renderMenu?: (row: ImapMessageRow, close: () => void) => ReactNode;
    /** The head's name, when it is not the folder's own (the combined Sent). */
    title?: string;
    /**
     * For a list drawn from several folders: a row's identity, the open one,
     * and the open call with the whole row. A UID only names a message inside
     * its own folder, so the merged list cannot go by it.
     */
    rowKey?: (row: ImapMessageRow) => string;
    openKey?: string | null;
    onOpenRow?: (row: ImapMessageRow) => void;
    /** A Sent folder: each row names who the letter went to, not who sent it. */
    recipients?: boolean;
    /** A short tag per row, such as the mailbox a merged row came from. */
    badge?: (row: ImapMessageRow) => string | null;
  }
>(function ImapMessageList(
  {
    path,
    rows,
    total,
    loading,
    reason,
    openUid,
    hasMore,
    onOpen,
    onShowMore,
    onReload,
    readingPane,
    onReadingPane,
    onRail,
    renderMenu,
    title,
    rowKey,
    openKey,
    onOpenRow,
    recipients,
    badge,
  },
  ref,
) {
  const t = useT();
  const menu = useContextMenu<ImapMessageRow>();
  // The last segment is the folder's name; the full path stays on the title, so
  // a deep folder does not eat the whole head.
  const name = title ?? (path.split(/[/.]/).pop() || path);

  return (
    <div ref={ref} className="crm-card crm-work-col crm-work-list">
      <div className="crm-pane-head">
        <RailTrigger label={t("mail.folders")} onOpen={onRail} />
        <span className="min-w-0 flex-1">
          <span className="crm-trunc block text-[13.5px] font-[650]" title={path}>
            {name}
          </span>
          {/* Nothing is claimed about a folder that has not answered yet. The
              first load has rows=0 and total=0, and "0 messages, the whole
              folder" is exactly the misreading this line exists to prevent. */}
          {reason ? null : (
            <span className="crm-mut mt-0.5 block text-[11.25px]">
              {loading && !rows.length
                ? t("email.imap.browse.loading")
                : total === rows.length
                  ? t("email.imap.browse.all", { n: fmtNum(total) })
                  : t("email.imap.browse.slice", { n: fmtNum(rows.length), total: fmtNum(total) })}
            </span>
          )}
        </span>
        {loading ? <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" /> : null}
        <IconBtn onClick={onReload} disabled={loading} title={t("email.imap.browse.reload")} aria-label={t("email.imap.browse.reload")}>
          <RefreshCw className="size-4" />
        </IconBtn>
        <ReadingPaneToggle pane={readingPane} onPane={onReadingPane} />
      </div>

      <div className="crm-pane-scroll">
        {reason ? (
          <div className="crm-pane-empty">{imapReasonText(t, reason, "email.imap.listFailed")}</div>
        ) : rows.length === 0 ? (
          <div className="crm-pane-empty">
            {loading ? t("email.imap.browse.loading") : t("email.imap.browse.empty")}
          </div>
        ) : (
          <>
            {rows.map((m) => {
              const sentTo = recipients ? m.to.join(", ") : "";
              const who = sentTo || m.fromName || m.fromAddress || t("email.imap.browse.noSender");
              const key = rowKey ? rowKey(m) : null;
              const on = key !== null ? key === openKey : m.uid === openUid;
              const tag = badge?.(m) ?? null;
              return (
                <button
                  key={key ?? m.uid}
                  type="button"
                  className={cn("crm-item", on && "is-on", !m.seen && "is-unread")}
                  onClick={() => (onOpenRow ? onOpenRow(m) : onOpen(m.uid))}
                  {...(renderMenu ? menu.rowProps(m) : {})}
                >
                  <Avatar name={who} />
                  <span className="min-w-0 flex-1">
                    <span className="crm-item-top">
                      <span className="crm-item-from">{who}</span>
                      <span className="crm-item-time">{fmtStamp(m.date)}</span>
                    </span>
                    <span className="crm-item-sub block">
                      {m.hasAttachments ? <Paperclip className="mr-1 inline size-3 align-[-1px] text-muted-foreground" /> : null}
                      {m.subject || t("email.imap.browse.noSubject")}
                    </span>
                    <span className="crm-item-pre block">
                      {tag ? <span className="crm-chip mr-1.5 align-[1px] text-[10.5px]">{tag}</span> : null}
                      {recipients ? t("mail.sentTo", { to: sentTo || "-" }) : m.fromAddress}
                    </span>
                  </span>
                </button>
              );
            })}

            {/* The cap is named rather than hidden: a folder can hold more than
                this pane will ever read, and the honest end of the list is a
                sentence saying where it stops. */}
            {hasMore ? (
              <Btn small className="mx-auto mt-2" onClick={onShowMore} disabled={loading}>
                {t("email.imap.browse.showMore")}
              </Btn>
            ) : rows.length < total ? (
              <p className="crm-pane-empty">{t("email.imap.browse.capped", { n: fmtNum(rows.length) })}</p>
            ) : null}
          </>
        )}
      </div>

      {/* One panel for the whole list, portalled to the body, exactly as the
          thread list does it - see `ThreadList` for why not one per row. */}
      {renderMenu ? (
        <ContextMenu menu={menu} label={t("email.imap.msg.menu")}>
          {renderMenu}
        </ContextMenu>
      ) : null}
    </div>
  );
});
